// src/utils/generateTimeSlots.js
import { DateTime } from 'luxon';
import { DROPS_TAB_NAME, TRAINING_TAB_NAME } from '../features/activeSwarms/constants';

/**
 * Formats a DateTime object to "MM/DD/YY h:mm AM/PM" in the user's local timezone.
 * @param {DateTime} dt - The DateTime object to format.
 * @returns {string} - Formatted date string or " - " if invalid.
 */
export function formatToLocalDateTime(dt) {
  if (!dt) return ' - ';

  try {
    if (!dt.isValid) return 'Invalid Date';

    // Convert to the user's local timezone
    const localDt = dt.toLocal();

    // Format as "MM/DD/YY h:mm AM/PM"
    return localDt.toFormat('MM/dd/yy h:mm a'); // Example: "11/21/24 4:00 PM"
  } catch (err) {
    console.log("Error formatting date with Luxon:", err);
    return " - ";
  }
}

/**
 * Generates an array of time slots based on the current time and swarms data.
 * @param {SwarmAdminToolRes[]} swarms - Array of swarms
 * @returns {Array<{ label: string, swarms: SwarmAdminToolRes[] }>} - Array of time slot objects
 */
export const generateTimeSlots = (swarms) => {
  // Create a shallow copy of swarms to avoid mutating the original array
  const swarmsAux = [...swarms];

  // Separate swarms into monitors and non-monitors
  const swarmsThatAreMonitorForDrop = swarmsAux.filter(swarm => swarm.isMonitor);
  const swarmsThatAreNotMonitorForDrop = swarmsAux.filter(swarm => !swarm.isMonitor);

  // Separate swarms into trainings and non-trainings
  const swarmsThatAreTraining = swarmsAux.filter(swarm => swarm.isTraining);
  const swarmsThatAreNotTraining = swarmsAux.filter(swarm => !swarm.isTraining);

  // Filter swarms that are not monitor and not training
  const swarmsThatAreNotMonitorForDropAndAreNotTraining = swarmsAux.filter(swarm => !swarm.isMonitor && !swarm.isTraining);  

  // Logging for debugging purposes
  console.log("SWARM_LOG: generateTimeSlots called with swarms:", swarms);
  console.log(`SWARM_LOG: ${swarmsThatAreMonitorForDrop.length} swarms are monitor for drop`);
  console.log(`SWARM_LOG: ${swarmsThatAreNotMonitorForDrop.length} swarms are NOT monitor for drop`);
  console.log(`SWARM_LOG: ${swarmsThatAreTraining.length} swarms are training`);
  console.log(`SWARM_LOG: ${swarmsThatAreNotTraining.length} swarms are NOT training`);
  console.log(`SWARM_LOG: ${swarmsThatAreNotMonitorForDropAndAreNotTraining.length} swarms are NOT monitor and NOT training`);

  // Get the current time in UTC using Luxon
  const now = DateTime.utc();
  console.log("SWARM_LOG: Current time (now, UTC):", now.toISO());

  // Get the time 15 minutes from now in UTC
  const nowPlus15 = now.plus({ minutes: 15 });
  console.log("SWARM_LOG: Current time plus 15 minutes (UTC):", nowPlus15.toISO());

  // Initialize the array to hold time slots
  const timeSlots = [];

  // Step 1: Create the "NOW and Current Hour" slot
  const firstSlotLabel = 'NOW';
  console.log("SWARM_LOG: First slot label:", firstSlotLabel);

  // Filter swarms for the first time slot
  const firstSlotSwarms = swarmsThatAreNotMonitorForDropAndAreNotTraining.filter(swarm => {
    // Parse salesDate as UTC using Luxon
    const salesDate = swarm.salesDate
      ? DateTime.fromISO(swarm.salesDate, { zone: 'utc' })
      : null;

    console.log(`SWARM_LOG: Evaluating Swarm ID ${swarm.id}: SalesDate (UTC) = ${salesDate ? salesDate.toISO() : 'null'}, IsNow = ${swarm.isNow}`);

    // Include swarms that are marked as "isNow" or whose salesDate is in the past or within the next 15 minutes
    return swarm.isNow || (salesDate && salesDate <= nowPlus15);
  });

  console.log(`SWARM_LOG: Swarms in first slot (${firstSlotLabel}):`, firstSlotSwarms);

  // If there are swarms for the first slot, add them to the timeSlots array
  if (firstSlotSwarms.length > 0) {
    timeSlots.push({
      label: firstSlotLabel,
      swarms: firstSlotSwarms,
    });
    console.log(`SWARM_LOG: Added first slot (${firstSlotLabel}) with ${firstSlotSwarms.length} swarms.`);
  } else {
    console.log(`SWARM_LOG: No swarms found for the first slot (${firstSlotLabel}).`);
  }

  const endOfCurrentDayInEstAsUTC = DateTime.now()
  .setZone('America/New_York') // Set to EST/EDT
  .endOf('day') // End of day in EST (23:59:59)
  .toUTC(); // Convert to UTC

  // Step 2: Create slots for the remaining swarms grouped by unique hour and minute
  const remainingSwarms = swarmsThatAreNotMonitorForDrop.filter(swarm => {
    const salesDate = swarm.salesDate
      ? DateTime.fromISO(swarm.salesDate, { zone: 'utc' })
      : null;
    // Ignore swarms that are running, not marked as "isNow", and have a salesDate older than the end of the current day in EST (as UTC)
    if (swarm.running && !swarm.isNow && salesDate && salesDate >= endOfCurrentDayInEstAsUTC) {
      return false; // Exclude these swarms
    }
    // Ignore swarms that are running == false and salesDate older than the end of the current day in EST (as UTC)
    if (!swarm.running && salesDate && salesDate >= endOfCurrentDayInEstAsUTC) {
      return false; // Exclude these swarms
    }
    return salesDate && !(swarm.isNow || (salesDate <= nowPlus15));
  });

  // Create a map to group swarms by their local hour and minute
  const timeSlotMap = new Map();

  remainingSwarms.forEach(swarm => {
    const salesDate = DateTime.fromISO(swarm.salesDate, { zone: 'utc' });
    // Format the time as "MM/DD/YY h:mm AM/PM"
    const timeKey = formatToLocalDateTime(salesDate); // "MM/DD/YY h:mm AM/PM"

    if (!timeSlotMap.has(timeKey)) {
      timeSlotMap.set(timeKey, []);
    }

    timeSlotMap.get(timeKey).push(swarm);
  });

   // Sort the slots by their local hour and minute
  const sortedTimeSlots = Array.from(timeSlotMap.entries()).sort(([keyA], [keyB]) => {
    const dateA = DateTime.fromFormat(keyA, 'MM/dd/yy h:mm a', { zone: 'local' });
    const dateB = DateTime.fromFormat(keyB, 'MM/dd/yy h:mm a', { zone: 'local' });
    return dateA - dateB;
  });

  sortedTimeSlots.forEach(([timeKey, swarmsGroup]) => {
    timeSlots.push({
      label: timeKey,
      swarms: swarmsGroup,
    });
  });


  // Step 3: Add the "Monitor for Drops" slot if there are monitor swarms
  if (swarmsThatAreMonitorForDrop.length > 0) {
    timeSlots.push({
      label: DROPS_TAB_NAME,
      swarms: swarmsThatAreMonitorForDrop,
    });
    console.log(`SWARM_LOG: Added slot Monitor for Drops with ${swarmsThatAreMonitorForDrop.length} swarms.`);
  }

  // Step 4: Add the "Training" slot if there are monitor swarms
  if (swarmsThatAreTraining.length > 0) {
    timeSlots.push({
      label: TRAINING_TAB_NAME,
      swarms: swarmsThatAreTraining,
    });
    console.log(`SWARM_LOG: Added slot Training Mode with ${swarmsThatAreTraining.length} swarms.`);
  }

  // Final log for debugging
  console.log("SWARM_LOG: Final timeSlots array:", timeSlots);

  // Return the array of time slots
  return timeSlots;
};

//A: sort swarms based on the sort option selected by the user
export const sortSwarms = (swarms, sortOption) => {
  switch (sortOption) {
    case 'highestReward':
      return [...swarms].sort((a, b) => b.reward - a.reward);
    case 'dateTime':
      return [...swarms].sort((a, b) => new Date(a.event.date) - new Date(b.event.date));
    case 'performerName':
      return [...swarms].sort((a, b) => a.event.name.localeCompare(b.event.name));
    case 'bteId':
    default:
      return [...swarms].sort((a, b) => a.bteId - b.bteId);
  }
};